import { useLinkProps } from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { useAtomValue } from "jotai";
import { useFragment } from "react-relay";

import { currentProfileAtom } from "@/features/Club/atoms";
import {
  isAccessibleBookingSlotAvailability,
  isOpenSlotAvailability,
} from "@/utils/slotAvailability";
import { BorderRadiusOption } from "@whoosh/design-system/src/apps/PatronApp/screens/teesheet/components/TeeSheet/TimeSlotCard/";
import { Box } from "@whoosh/design-system/src/components/primitive";
import { dateUtils } from "@whoosh/utilities";
import { formatServerDateTimeHHMMWithAgendaDate } from "@whoosh/utilities/src/dates";

import type {
  FacilityMemberPermissions,
  MemberAccess,
} from "../../../../../../hooks/getFacilityMemberPermissions";
import { displayHoleOffset } from "../../../../../../utils/holeCounts/displayHoleOffset";
import { isBookingMultipleTeeTimesAtom } from "../../../../atoms";
import { TimeSlotRow_Timeslot$key } from "./__generated__/TimeSlotRow_Timeslot.graphql";
import { PrivateTimeSlotCardWrapper } from "./components/PrivateTimeSlotCardWrapper";
import { PublicTimeCardWrapper } from "./components/PublicTimeSlotCardWrapper";

export type TimeSlotRowProps = {
  onSlotPress: (slotId: string) => void;
  isCurrentSlot?: boolean;
  players: {
    id: string;
    firstName: string;
    lastName: string;
    type: "Member" | "Guest";
  }[];
  shouldShowCourseName: boolean;
  timeSlotRef: TimeSlotRow_Timeslot$key;
  facilityPermissions: FacilityMemberPermissions;
  position?: "first" | "last" | "middle" | "only";
  clubVisibility?: "PUBLIC" | "PRIVATE" | "SEMI_PRIVATE";
};

export const canOpenTimeSlot = ({
  bookingsLength,
  canBookOnBusySlot,
  canUpdate,
  clubVisibility,
  hasWriteAccess,
  isAccessibleBooking,
  isBookingMultipleTeeTimes,
  isFree,
  memberAccess,
  openSlots,
}: {
  bookingsLength: number;
  canBookOnBusySlot: boolean | null | undefined;
  canUpdate: boolean;
  clubVisibility?: "PUBLIC" | "PRIVATE" | "SEMI_PRIVATE";
  hasWriteAccess: boolean;
  isAccessibleBooking: boolean;
  isBookingMultipleTeeTimes: boolean;
  isFree: boolean;
  memberAccess: MemberAccess;
  openSlots: number;
}) => {
  const hasBookingAccess = ["JOINABLE", "VISIBLE", "HIDDEN"].includes(
    memberAccess
  );

  if (!hasBookingAccess) {
    return false;
  }

  if (isBookingMultipleTeeTimes) {
    return bookingsLength === 0 && isFree;
  }

  return (
    canUpdate ||
    isFree ||
    (isAccessibleBooking && hasWriteAccess) ||
    (isAccessibleBooking &&
      openSlots > 0 &&
      (clubVisibility !== "PRIVATE" || Boolean(canBookOnBusySlot)))
  );
};

export const getDisplayedOpenSlots = ({
  isAccessibleBooking,
  isFree,
  memberAccess,
  openSlots,
}: {
  isAccessibleBooking: boolean;
  isFree: boolean;
  memberAccess: MemberAccess;
  openSlots: number;
}) => {
  if (isFree) {
    return openSlots;
  }

  if (isAccessibleBooking && memberAccess === "JOINABLE") {
    return openSlots;
  }

  return 0;
};

export const TimeSlotRow = ({
  onSlotPress,
  players,
  shouldShowCourseName,
  timeSlotRef,
  facilityPermissions,
  position = "only",
  clubVisibility,
}: TimeSlotRowProps) => {
  const isBookingMultipleTeeTimes = useAtomValue(isBookingMultipleTeeTimesAtom);
  const currentProfile = useAtomValue(currentProfileAtom);

  const timeSlot = useFragment(TimeSlotRowFragmentString, timeSlotRef);
  const bookings =
    timeSlot.bookings?.edges
      ?.map((e) => e?.node)
      .filter((b) => b?.status !== "DRAFT") ?? [];

  const isFree = isOpenSlotAvailability(timeSlot.availability);

  const viewerMemberId = currentProfile?.memberId;
  // A viewer may own a booking without having a round in it (e.g. when
  // reserving multiple tee times to host a larger group, subsequent
  // bookings may have no viewer round but the viewer is still the owner).
  // Treat owned-but-not-playing bookings the same as playing-in for the
  // purposes of routing the card press to the booking detail screen.
  const currentUsersBooking = bookings.find((booking) => {
    if (!viewerMemberId) return false;
    const ownerClient = booking?.ownerClient;
    if (
      ownerClient &&
      "id" in ownerClient &&
      ownerClient.id === viewerMemberId
    ) {
      return true;
    }
    return booking?.rounds?.edges?.some(
      (edge) => edge?.node?.player?.id === viewerMemberId
    );
  });
  const bookingId = currentUsersBooking?.id ?? bookings?.[0]?.id;
  const isCurrentUserInBooking = Boolean(currentUsersBooking);

  const bookingSummaryLinkProps = useLinkProps({
    screen: "BookingSummary",
    params: {
      slotOrBookingId: bookingId,
    },
  });

  const handleSlotSelection = () => {
    if (isCurrentUserInBooking && bookingId) {
      bookingSummaryLinkProps.onPress();
    } else {
      onSlotPress(timeSlot.id);
    }
  };

  const openSlots = (timeSlot.capacity || 4) - (timeSlot.usedCapacity || 0);

  const hasWriteAccess = bookings.some(
    (b) => b?.writeAccess === "JOIN" || b?.writeAccess === "FULL_WRITE"
  );

  const { memberAccess, canBookOnBusySlot } = facilityPermissions;

  const canUpdate = bookings.some((b) => b?.canUpdate) || false;
  const isAccessibleBooking = isAccessibleBookingSlotAvailability(
    timeSlot.availability
  );

  const canOpenSlot = canOpenTimeSlot({
    bookingsLength: bookings.length,
    canBookOnBusySlot,
    canUpdate,
    clubVisibility,
    hasWriteAccess,
    isAccessibleBooking,
    isBookingMultipleTeeTimes,
    isFree,
    memberAccess,
    openSlots,
  });
  const displayedOpenSlots = getDisplayedOpenSlots({
    isAccessibleBooking,
    isFree,
    memberAccess,
    openSlots,
  });

  const timeString =
    (timeSlot.dateTime
      ? formatServerDateTimeHHMMWithAgendaDate(
          timeSlot.dateTime,
          timeSlot.agenda.date
        )
      : dateUtils.formatHHMM(timeSlot.time)) || undefined;

  const courseName = shouldShowCourseName ? timeSlot.course.name : undefined;

  const holeOffset = displayHoleOffset(
    timeSlot.holeOffset,
    timeSlot.groupLabel ?? undefined
  );

  const locationString = courseName
    ? holeOffset
      ? `${courseName} - ${holeOffset}`
      : courseName
    : holeOffset
    ? holeOffset
    : undefined;

  const borderRadius: { top: BorderRadiusOption; bottom: BorderRadiusOption } =
    {
      top: ["first", "only"].includes(position) ? "2" : "0",
      bottom: ["last", "only"].includes(position) ? "2" : "0",
    };

  return (
    <Box mt={openSlots > 0 || clubVisibility === "PRIVATE" ? 1 : 0}>
      {clubVisibility === "PRIVATE" ? (
        <PrivateTimeSlotCardWrapper
          timeSlotRef={timeSlot}
          players={players}
          time={timeString}
          onPress={canOpenSlot ? handleSlotSelection : undefined}
          borderRadius={borderRadius}
          locationString={locationString}
          availableSpots={displayedOpenSlots}
          canBookOnBusySlot={canBookOnBusySlot}
        />
      ) : (
        <PublicTimeCardWrapper
          time={timeString}
          availableSpots={displayedOpenSlots}
          isRequestAvailable={false}
          onPress={canOpenSlot ? handleSlotSelection : undefined}
          borderRadius={borderRadius}
          location={locationString}
          timeSlotRef={timeSlot}
          position={position}
        />
      )}
    </Box>
  );
};

export const TimeSlotRowFragmentString = graphql`
  fragment TimeSlotRow_Timeslot on TimeSlot
  @argumentDefinitions(membershipTypeId: { type: "ID!", defaultValue: "" }) {
    id
    time
    dateTime
    agenda {
      date
    }
    availability
    holeOffset
    groupLabel
    capacity(bookingType: STANDARD)
    usedCapacity
    block {
      id
    }
    ...PublicTimeSlotCardWrapper_Timeslot
      @include(if: $isCustomer)
      @arguments(membershipTypeId: $membershipTypeId)
    ...PrivateTimeSlotCardWrapper_timeSlot
    course {
      id
      name
    }
    bookings {
      edges {
        node {
          id
          canUpdate
          writeAccess
          ownerClient {
            __typename
            ... on Member {
              id
            }
            ... on Customer {
              id
            }
          }
          ... on StandardBooking {
            status
          }
          ... on DiningBooking {
            status
          }
          ... on LessonBooking {
            status
          }
          rounds(noShowStatus: [PRESENT]) {
            edges {
              node {
                player {
                  id
                }
              }
            }
          }
        }
      }
    }
  }
`;
